import React, { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const DISMISS_KEY = "gt_install_banner_dismissed_at";
const RESHOW_MS = 30 * 24 * 60 * 60 * 1000; // re-show 30 days after dismissal

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallBanner({ onInstall }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    // Migrate legacy permanent dismissal into a timestamp so it re-shows after 30 days.
    if (localStorage.getItem("gt_install_banner_dismissed") === "1" && !localStorage.getItem(DISMISS_KEY)) {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      localStorage.removeItem("gt_install_banner_dismissed");
    }
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < RESHOW_MS) return;
    const visits = parseInt(localStorage.getItem("gt_visits") || "0", 10) + 1;
    localStorage.setItem("gt_visits", String(visits));
    if (visits >= 2) {
      const tmr = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(tmr);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div data-testid="install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md">
      <div className="flex items-center gap-3 rounded-[14px] border border-[#007BFF]/40 bg-[#0F0F0F]/95 backdrop-blur-md px-4 py-3 shadow-2xl gt-glow-blue">
        <Download size={18} className="text-[#00E676] shrink-0" />
        <span className="flex-1 text-sm text-white/85 truncate">{t("banner_text")}</span>
        <button data-testid="install-banner-btn" onClick={() => { onInstall && onInstall(); dismiss(); }}
          className="shrink-0 bg-[#007BFF] hover:bg-[#0056b3] text-white text-sm font-semibold rounded-[10px] px-3 py-1.5 transition-colors">
          {t("banner_btn")}
        </button>
        <button data-testid="install-banner-close" onClick={dismiss} className="shrink-0 text-white/40 hover:text-white transition-colors">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
